/**
 * useBpmPulse
 *
 * Detects heartbeat BPM via microphone using autocorrelation on the audio
 * time-domain data. Falls back to a calm 70 BPM baseline when:
 *   — mic permission is denied
 *   — heartbeat cannot be reliably detected
 *   — the hook is idle
 *
 * Returns:
 *   bpm          — current beats per minute (smoothed)
 *   isListening  — mic is active and analysing
 *   isHeartbeat  — a reliable heartbeat was detected (vs baseline)
 *   toggleMic    — request / release mic permission
 *
 * Privacy: no data is stored or transmitted — real-time only.
 * Performance: lightweight autocorrelation on 512-sample windows at ~10 fps.
 */

import { useState, useRef, useCallback, useEffect } from "react";

const BASELINE_BPM = 70;
const MIN_BPM = 40;
const MAX_BPM = 180;
const SAMPLE_RATE_HZ = 10;          // analysis frames per second
const SMOOTHING_ALPHA = 0.15;       // EMA weight for new BPM readings

/** Autocorrelation-based period estimator on a Float32Array waveform. */
function estimateBpm(buf: Float32Array, sampleRate: number): number | null {
  const n = buf.length;
  // Find max energy window
  const minPeriod = Math.floor(sampleRate * 60 / MAX_BPM);
  const maxPeriod = Math.floor(sampleRate * 60 / MIN_BPM);

  let bestCorr = -Infinity;
  let bestPeriod = -1;

  for (let lag = minPeriod; lag <= maxPeriod && lag < n; lag++) {
    let corr = 0;
    for (let i = 0; i < n - lag; i++) {
      corr += buf[i] * buf[i + lag];
    }
    if (corr > bestCorr) {
      bestCorr = corr;
      bestPeriod = lag;
    }
  }

  if (bestPeriod < 0 || bestCorr < 0.01) return null;
  return (sampleRate / bestPeriod) * 60;
}

export function useBpmPulse() {
  const [bpm, setBpm] = useState(BASELINE_BPM);
  const [isListening, setIsListening] = useState(false);
  const [isHeartbeat, setIsHeartbeat] = useState(false);

  const streamRef   = useRef<MediaStream | null>(null);
  const ctxRef      = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const smoothedBpm = useRef(BASELINE_BPM);

  const stopMic = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (analyserRef.current) {
      try { analyserRef.current.disconnect(); } catch { /* noop */ }
      analyserRef.current = null;
    }
    if (ctxRef.current) {
      try { ctxRef.current.close(); } catch { /* noop */ }
      ctxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsListening(false);
    setIsHeartbeat(false);
    setBpm(BASELINE_BPM);
    smoothedBpm.current = BASELINE_BPM;
  }, []);

  const startMic = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      streamRef.current = stream;

      const ctx = new AudioContext();
      ctxRef.current = ctx;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.8;
      analyserRef.current = analyser;

      const source = ctx.createMediaStreamSource(stream);
      // Do NOT connect to destination — we don't want mic audio playing back
      source.connect(analyser);

      const buf = new Float32Array(analyser.fftSize);

      intervalRef.current = setInterval(() => {
        analyser.getFloatTimeDomainData(buf);

        // RMS energy gate — only analyse if signal is present
        let energy = 0;
        for (let i = 0; i < buf.length; i++) energy += buf[i] * buf[i];
        const rms = Math.sqrt(energy / buf.length);

        if (rms < 0.003) {
          // Too quiet — drift back to baseline over time
          smoothedBpm.current =
            smoothedBpm.current * (1 - SMOOTHING_ALPHA) + BASELINE_BPM * SMOOTHING_ALPHA;
          setBpm(Math.round(smoothedBpm.current));
          setIsHeartbeat(false);
          return;
        }

        const raw = estimateBpm(buf, ctx.sampleRate);
        if (raw !== null && raw >= MIN_BPM && raw <= MAX_BPM) {
          smoothedBpm.current =
            smoothedBpm.current * (1 - SMOOTHING_ALPHA) + raw * SMOOTHING_ALPHA;
          setBpm(Math.round(smoothedBpm.current));
          setIsHeartbeat(true);
        }
      }, 1000 / SAMPLE_RATE_HZ);

      setIsListening(true);
    } catch {
      // Permission denied or API unavailable — stay on baseline
      stopMic();
    }
  }, [stopMic]);

  const toggleMic = useCallback(() => {
    if (isListening) {
      stopMic();
    } else {
      startMic();
    }
  }, [isListening, startMic, stopMic]);

  // Clean up on unmount
  useEffect(() => () => { stopMic(); }, [stopMic]);

  return { bpm, isListening, isHeartbeat, toggleMic };
}
